import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { describeResult, isSuccess } from '../lib/packingListRequest';
import { workspaceKey } from '../lib/packingListWire';

/**
 * 문서 한 건의 작업 이력. Drawer가 열려 있는 동안만 읽고, 팀원의 변경 알림·재연결 때 다시 읽는다.
 * 거절(연결 끊김 포함)은 스낵바 대신 Drawer 안에서 Retry와 함께 알린다. 거절 code도 넘겨, 문서가 지워진 경우를 화면이 따로 다룬다.
 * 다른 문서로 바꾸면 이전 문서의 이력을 바로 비우고, 옛 응답은 버린다. 같은 문서의 재조회는 보던 내용을 유지한다.
 */
export default function usePackingListHistory(commands, { docId, scope, enabled = true }) {
    const [state, setState] = useState({ status: 'idle', data: null, error: null, code: null, docId: null });
    const requestSeq = useRef(0);
    const changeSeq = useSelector((s) => (scope ? s.packingList.changeSeq[workspaceKey(scope.tab, scope.houseId)] ?? 0 : 0));
    const connectionEpoch = useSelector((s) => s.socketStatus.connectionEpoch);

    const load = useCallback(async () => {
        const seq = requestSeq.current + 1;
        requestSeq.current = seq;
        if (!enabled || docId == null) {
            setState({ status: 'idle', data: null, error: null, code: null, docId: null });
            return null;
        }
        setState((s) => ({ status: 'loading', data: s.docId === docId ? s.data : null, error: null, code: null, docId }));
        const result = await commands.readHistory({ id: docId, quiet: true });
        if (seq !== requestSeq.current) return result;
        if (isSuccess(result)) setState({ status: 'ready', data: result.data, error: null, code: null, docId });
        else setState({ status: 'error', data: null, error: describeResult(result), code: result?.code ?? null, docId });
        return result;
    }, [commands, docId, enabled]);

    useEffect(() => {
        load();
    }, [load, changeSeq, connectionEpoch]);

    const { docId: _loadedDocId, ...view } = state;
    return { ...view, reload: load };
}
